import React, {ReactElement, ReactNode, RefObject} from 'react';
import styled, {css} from 'styled-components';
import {theme} from '../style';
import {cancel, blueDownArrow} from './index';
import {getMuiV4Theme} from 'utils/css/themeUtils';
import Loading from 'components/pure/utils/Loading';
import {debounceAsync} from 'utils/debounceAsync';

export interface MultiselectDropdownOption {
  id: number | string;
  name: string;
  color?: string;
}

export enum SelectedStyle {
  PILL_STYLE,
  STACKED_STYLE,
}

/**
 *
 */
export type MultiselectDropdownProps = {
  // Selected values
  selectedValues: MultiselectDropdownOption[];
  // Pass any possible selections you have available
  syncSelections: MultiselectDropdownOption[];
  // Pass any pre-selected items you'd like, optional
  // Note! If you ever change these, we forcefully set these and only these as the selections!
  preloadedSelections?: MultiselectDropdownOption[];
  // A source to get more from the server (we'll debounce)
  // If no async source provided, the sync selections will populate for typeahead.
  asyncSelectionsSource?: (i: string) => Promise<MultiselectDropdownOption[]>;
  // Callback when an item is selected
  onSelect: (s: MultiselectDropdownOption) => void;
  // Callback when an item is removed
  onRemoveSelection: (s: MultiselectDropdownOption) => void;
  renderSelection?: (
    s: MultiselectDropdownOption,
    onSelect: (s: MultiselectDropdownOption) => void
  ) => ReactNode;
  renderSelectedValue?: (
    s: MultiselectDropdownOption,
    onRemove: (s: MultiselectDropdownOption) => void
  ) => ReactNode;
  // Placeholder text. optional
  placeholderText?: string;
  noResultsPlaceholderText?: string;
  // optional amt before we fetch remote. defaults to 2
  keystrokesBeforeAsyncFetch?: number;
  // optional function - use this to filter potential async values against the current selected values
  dupeFinder?: (v: MultiselectDropdownOption, vs: MultiselectDropdownOption[]) => boolean;
  // pill or stacked?
  selectedStyle?: SelectedStyle;
  // pass a custom arrow to use
  arrow?: (open: boolean, onShowDropdown: () => void) => ReactElement;
  // custom height for dropdown, defaults to 200px
  dropdownHeight?: number;
};

type MultiselectDropdownState = {
  open: boolean;
  loading: boolean;
  searchValue?: string;
  settledTypeaheadSelections: MultiselectDropdownOption[];
};

const height = 32;
const defaultDropdownHeight = 200;
const defaultKeystrokes = 2;
const defaultDupeFinder = (s: MultiselectDropdownOption, vs: MultiselectDropdownOption[]) =>
  vs.find(v => v.id === s.id || v.name.localeCompare(s.name) === 0);

export default class MultiselectDropdown extends React.Component<
  MultiselectDropdownProps,
  MultiselectDropdownState
> {
  state: MultiselectDropdownState = {
    open: false,
    loading: false,
    searchValue: undefined,
    settledTypeaheadSelections: [],
  };

  private readonly ref: RefObject<HTMLDivElement>;

  private asyncSource?: (i: string) => Promise<MultiselectDropdownOption[]>;

  constructor(props: MultiselectDropdownProps) {
    super(props);

    this.ref = React.createRef<HTMLDivElement>();

    this.onChange = this.onChange.bind(this);
    this.onShowDropdown = this.onShowDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.onSelectValue = this.onSelectValue.bind(this);
    this.onRemoveValue = this.onRemoveValue.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, true);

    const {asyncSelectionsSource} = this.props;

    this.asyncSource = asyncSelectionsSource
      ? debounceAsync(asyncSelectionsSource, 500)
      : undefined;
  }

  onShowDropdown() {
    this.setState({open: !this.state.open, searchValue: undefined});
  }

  handleClickOutside(event: MouseEvent) {
    if (event.target instanceof HTMLElement && !this.ref.current?.contains(event.target)) {
      this.setState({open: false});
    }
  }

  /**
   * Do we have enough typeahead characters to execute typeahead search, basically
   *
   * @param s
   */
  typeaheadSearchConditionsMet(s: string) {
    const {keystrokesBeforeAsyncFetch = defaultKeystrokes} = this.props;

    return s && s.length >= keystrokesBeforeAsyncFetch;
  }

  onChange(v: string) {
    const {dupeFinder = defaultDupeFinder, syncSelections, selectedValues} = this.props;
    this.setState({searchValue: v});

    if (this.typeaheadSearchConditionsMet(v)) {
      this.setState({loading: true, open: true, settledTypeaheadSelections: []}, async () => {
        if (this.asyncSource) {
          this.asyncSource(v).then(rslt => {
            this.setState({
              settledTypeaheadSelections: rslt.filter(r => !dupeFinder(r, selectedValues)),
              loading: false,
            });
          });
        } else {
          this.setState({
            settledTypeaheadSelections: syncSelections.filter(s =>
              s.name.toLowerCase().includes(v.toLowerCase())
            ),
            loading: false,
          });
        }
      });
    } else {
      this.setState({open: false});
    }
  }

  onSelectValue(i: MultiselectDropdownOption) {
    const {selectedValues} = this.props;
    const {dupeFinder = defaultDupeFinder} = this.props;

    const isADupe = dupeFinder(i, selectedValues);

    if (isADupe) return;

    this.setState({
      open: false,
      searchValue: undefined,
    });

    this.props.onSelect(i);
  }

  onRemoveValue(i: MultiselectDropdownOption) {
    this.setState({
      open: false,
      searchValue: undefined,
    });

    this.props.onRemoveSelection(i);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  }

  render() {
    const {
      syncSelections,
      placeholderText,
      noResultsPlaceholderText = 'No results.',
      renderSelection,
      renderSelectedValue,
      dupeFinder = defaultDupeFinder,
      selectedStyle = SelectedStyle.STACKED_STYLE,
      arrow = (open: boolean, onShowDropdown: () => void) => (
        <DropdownIcon
          fill={getMuiV4Theme().palette.primary.main}
          viewBox={'0 0 330 330'}
          transform={open ? 'rotate(180)' : undefined}
          onClick={onShowDropdown}>
          {blueDownArrow}
        </DropdownIcon>
      ),
      dropdownHeight = defaultDropdownHeight,
      selectedValues,
    } = this.props;
    const {open, searchValue, settledTypeaheadSelections, loading} = this.state;

    const dropdownValues = (searchValue ? settledTypeaheadSelections : syncSelections).filter(
      r => !dupeFinder(r, selectedValues)
    );

    // show our no results placeholder
    // 1. if sync and no more options to select
    // 2. if async and not loading and no options to select
    const showNoResults =
      (open && !searchValue && dropdownValues.length === 0) ||
      (open && searchValue && !loading && dropdownValues.length === 0);

    const SelectedStyleContainer =
      selectedStyle === SelectedStyle.STACKED_STYLE
        ? StackedSelectedListWrapper
        : PillSelectedListWrapper;
    const SelectedStyleItemContainer =
      selectedStyle === SelectedStyle.STACKED_STYLE
        ? StackedSelectedStyleItemContainer
        : PillSelectedStyleItemContainer;

    return (
      <Wrapper ref={this.ref}>
        <InputWithIcon>
          <TextInput
            type={'text'}
            value={searchValue || ''}
            onChange={({target}) => this.onChange(target.value)}
            placeholder={placeholderText}
          />
          {arrow(open, this.onShowDropdown)}
          {loading && (
            <SpinnerContainer>
              <Loading />
            </SpinnerContainer>
          )}
          {open && dropdownValues.length > 0 && (
            <Dropdown dropdownHeight={dropdownHeight}>
              <UnstyledList>
                {dropdownValues.map(i =>
                  renderSelection ? (
                    renderSelection(i, this.onSelectValue)
                  ) : (
                    <DropdownItem key={i.name} onClick={() => this.onSelectValue(i)}>
                      {i.name}
                    </DropdownItem>
                  )
                )}
              </UnstyledList>
            </Dropdown>
          )}
          {showNoResults && (
            <Dropdown dropdownHeight={dropdownHeight}>
              <UnstyledList>
                <DropdownRow>{noResultsPlaceholderText}</DropdownRow>
              </UnstyledList>
            </Dropdown>
          )}
        </InputWithIcon>
        <SelectedWrapper>
          <SelectedStyleContainer>
            {selectedValues.map(v =>
              renderSelectedValue ? (
                renderSelectedValue(v, this.onRemoveValue)
              ) : (
                <SelectedStyleItemContainer key={v.name} highlightColor={v.color}>
                  <label>{v.name}</label>
                  <RemoveIcon highlightColor={v.color} onClick={() => this.onRemoveValue(v)}>
                    {cancel}
                  </RemoveIcon>
                </SelectedStyleItemContainer>
              )
            )}
          </SelectedStyleContainer>
        </SelectedWrapper>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  font-family: ${theme.fonts.primaryFontFamily};
  color: ${theme.colors.basicallyBlack};
`;

const InputWithIcon = styled.div`
  height: ${height}px;
  position: relative;
  box-sizing: border-box;
`;

const TextInput = styled.input`
  height: 100%;
  width: 100%;
  padding: 4px ${height * 2}px 4px 4px;
  box-sizing: border-box;
  outline: none;
  border: 1px solid ${theme.colors.chartLineSecondary};
  border-radius: 3px;

  :focus {
    outline: none;
    border: 1px solid ${theme.colors.chartLinePrimary};
  }
`;

const DropdownIcon = styled.svg<{fill?: string}>`
  height: ${height - 4}px;
  position: absolute;
  right: 2px;
  top: 2px;
  fill: ${({fill}) => fill};
  cursor: pointer;
`;

const RemoveIcon = styled.svg<{highlightColor?: string}>`
  height: 24px;
  width: 24px;
  fill: ${({highlightColor}) => (highlightColor ? 'white' : theme.colors.basicallyBlack)};
  cursor: pointer;
`;

const SpinnerContainer = styled.span`
  position: absolute;
  right: ${height - 2}px;
  top: -24px;
  fill: ${theme.colors.primaryBlue};
  cursor: pointer;

  .ev-loading {
    transform: scale(0.5);
  }
`;

const UnstyledList = styled.ul`
  padding: 0;
  margin: 5px 0 0 0;
  list-style-type: none;
`;

const StackedSelectedListWrapper = UnstyledList;
const PillSelectedListWrapper = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 5px 0;
  margin: 0;
  list-style-type: none;
`;

const DropdownRow = styled.li`
  margin-bottom: 5px;
  padding: 5px;
  transition: 100ms ease-out;
`;

const DropdownItem = styled(DropdownRow)`
  cursor: pointer;

  :hover {
    background-color: ${theme.colors.chartBackground};
  }
`;

const Dropdown = styled.div<{dropdownHeight: number}>`
  position: absolute;
  top: ${height + 5}px;
  left: 0;
  max-height: ${({dropdownHeight}) => dropdownHeight + 'px'};
  width: 100%;
  overflow-x: hidden;
  border: 1px solid ${theme.colors.chartLineSecondary};
  background: #fcfcfc;
  z-index: 1;
`;

const SelectedWrapper = styled.div`
  padding-top: 5px;
`;

const StackedSelectedStyleItemContainer = styled.li<{highlightColor?: string}>`
  ${({highlightColor}) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px;
    margin: 5px 0;
    background-color: ${highlightColor ?? theme.colors.chartBackground};
    color: ${highlightColor ? 'white' : theme.colors.basicallyBlack};
    border-radius: 3px;
  `}
`;

const PillSelectedStyleItemContainer = styled.li<{highlightColor?: string}>`
  ${({highlightColor}) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px;
    margin-bottom: 5px;
    background-color: ${highlightColor ?? theme.colors.chartBackground};
    color: ${highlightColor ? 'white' : theme.colors.basicallyBlack};
    border-radius: 3px;

    &:not(last-child) {
      margin-right: 5px;
    }

    label {
      padding-right: 3px;
    }
  `}
`;
